import {
  CarSession,
  CarSessionEntitlement,
  FeeType,
  Site,
  useFindSessionBySiteAndVrmMutation,
  UserPlate,
} from '../../generated/graphql';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useVrm } from '../../shared/hooks/useVrm';
import { ClipLoader } from 'react-spinners';
import { useRef, useState } from 'react';
import { ZONE } from '../../shared/constants';
import { DateTime } from 'luxon';
import {
  calculateAmount,
  roundToClosestMultiple,
} from '../../shared/utils/calculator';
import { colors } from '../../styles';
import { useReceiveCommunication } from '../../shared/hooks/useReceiveCommunication';
import { Tooltip } from '../../shared/components/Tooltip';
import { useStepHandler } from '../../shared/hooks/useStepHandler';
import { CardToken } from '../../shared/types/card-token';
import {
  getSiteSpecialTariffs,
  isCurrentTariffPostPayment,
} from '../../shared/utils/specialTariff';
import { getSpecialTariffText } from '../../shared/utils/specialTariffs';
import { t } from 'i18next';
import { Home } from '.';
import { PaymentFlow } from '../../shared/components/PaymentFlow/PaymentFlow';
import { CheckCircle } from '@mui/icons-material';

const FREE_GREEN = '#2e7d32';

interface IProps {
  site: Site;
  specialTariffMessage: string | undefined;
}

const getEffectiveGrantedMinutes = (
  sessionStart: number,
  sessionEnd: number,
  entitlements?: CarSessionEntitlement[] | null
): number => {
  if (!entitlements || entitlements.length === 0) {
    return 0;
  }

  const sessionMinutes = Math.max(0, Math.floor((sessionEnd - sessionStart) / 60));
  if (sessionMinutes === 0) {
    return 0;
  }

  // A credit that touches the session counts in full: its window only says
  // where in the stay it sits (the Good Things webhook anchors it right after
  // the free time), not how much of it has elapsed when the driver pays.
  const totalGranted = entitlements.reduce((sum, entitlement) => {
    const touchesSession =
      Math.min(sessionEnd, entitlement.validTo) >
      Math.max(sessionStart, entitlement.validFrom);
    return sum + (touchesSession ? entitlement.minutes : 0);
  }, 0);

  return Math.min(totalGranted, sessionMinutes);
};

export const PostPayment: React.FC<IProps> = ({
  site,
  specialTariffMessage,
}: IProps) => {
  const { vrm, VrmComponent, setVrm } = useVrm();
  const [loadingPayment, setLoadingPayment] = useState<boolean>();
  const [checkedCreditCard, setCheckedCreditCard] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [tokens, setTokens] = useState<CardToken[]>([]);
  const [plates, setPlates] = useState<UserPlate[]>([]);
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [selectedVrm, setSelectedVrm] = useState<string>('new');
  const [findSessionBySiteAndVrm, { loading: loadingSession }] =
    useFindSessionBySiteAndVrmMutation();
  const [carSession, setCarSession] = useState<
    (CarSession & {
      amount: number;
      duration: number;
      entitlementGrantedMinutes: number;
      noCharge: boolean;
    }) | null
  >(null);
  const [showPaymentFlow, setShowPaymentFlow] = useState<boolean>(false);
  const bottomRef = useRef<null | HTMLButtonElement>(null);
  const {
    email,
    setEmail,
    EmailComponent,
    phone,
    setPhone,
    PhoneComponent,
    receiveEmails,
    setReceiveEmails,
    receiveSms,
    setReceiveSms,
    ReceiveEmailComponent,
    ReceiveSMSComponent,
  } = useReceiveCommunication();
  const {
    payWithTokenLoading,
    paymentLinkLoading,
    validateMfaLoading,
    requestToken,
    mfa,
    setMfa,
    MFAComponent,
    TimerComponent,
    step,
    setStep,
    processPayment,
    hasTokenLoading,
  } = useStepHandler(
    setError,
    bottomRef,
    setTokens,
    setPlates,
    setSelectedCard,
    setSelectedVrm,
    setVrm
  );
  const [selectedFee, setSelectedFee] = useState<FeeType>(FeeType.Hourly);
  const [specialTariffSelected, setSpecialTariffSelected] = useState<
    number | null
  >(null);

  const handleSearch = async () => {
    try {
      setError('');
      setCarSession(null);
      const result = await findSessionBySiteAndVrm({
        variables: {
          siteId: site.id,
          vrm: vrm,
        },
      });
      const { data } = result;
      if (data) {
        const { findSessionBySiteAndVrm } = data;
        if (!findSessionBySiteAndVrm) {
          setError(t('noSessionFoundSelectParkingTime') || 'No session found');
          return;
        }
        if (findSessionBySiteAndVrm.letOut) {
          setError(
            findSessionBySiteAndVrm.notification ??
              (t('carCanLeave') || 'Car can leave')
          );
          return;
        } else {
          let currentFeeType = FeeType.Hourly;
          const tariffs = getSiteSpecialTariffs(site);
          let selectedSpecialTariff = null;
          const startTime = DateTime.fromSeconds(
            findSessionBySiteAndVrm.startTime,
            { zone: ZONE }
          );
          if (tariffs.length > 0) {
            const currentTarrif = tariffs.find((x) => {
              return isCurrentTariffPostPayment(x, startTime);
            });
            if (currentTarrif) {
              currentFeeType = FeeType.SpecialTariff;
              setSelectedFee(FeeType.SpecialTariff);
              setSpecialTariffSelected(currentTarrif.id);
              selectedSpecialTariff = currentTarrif.id;
            }
          }

          setError('');
          const roundedSessionMinutes = roundToClosestMultiple(
            site.timeStep,
            startTime.diffNow().as('minutes') * -1
          );
          const sessionEnd = Math.floor(DateTime.now().setZone(ZONE).toSeconds());
          const effectiveGranted = getEffectiveGrantedMinutes(
            findSessionBySiteAndVrm.startTime,
            sessionEnd,
            findSessionBySiteAndVrm.entitlements
          );
          const duration = Math.max(0, roundedSessionMinutes - effectiveGranted);
          const amount = calculateAmount(
            site,
            currentFeeType,
            duration,
            false,
            selectedSpecialTariff,
            startTime
          );
          // Nothing to pay for parking (free time and/or Good Things credits):
          // the card fee is not charged on its own, as on the exit kiosk.
          const noCharge = amount - (site.transactionFee ?? 0) <= 0;
          console.log('[postpayment] amount inputs', {
            siteId: site.id,
            vrm,
            paymentType: site.paymentType,
            startTime: startTime.toISO(),
            rawMinutes: startTime.diffNow().as('minutes') * -1,
            timeStep: site.timeStep,
            roundedSessionMinutes,
            effectiveGranted,
            duration,
            hasFreeTime: site.hasFreeTime,
            freeTime: site.freeTime,
            costPerHour: site.costPerHour,
            costPerHourWeekend: site.costPerHourWeekend,
            pricingTiers: site.pricingTiers,
            currentFeeType,
            selectedSpecialTariff,
            amount,
          });
          setCarSession({
            ...findSessionBySiteAndVrm,
            amount: noCharge ? 0 : amount,
            duration,
            entitlementGrantedMinutes: effectiveGranted,
            noCharge,
          });
        }
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.log('Error', error);
      setError(
        t('sessionSearchError') ||
          'There was an error searching for the session'
      );
    }
  };

  const handleCreditCardChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedCreditCard(event.target.checked);
    if (event.target.checked) {
      setReceiveSms(true);
    }
  };

  const canBuy = carSession && carSession.duration > 0;
  const loadingButton =
    validateMfaLoading ||
    paymentLinkLoading ||
    hasTokenLoading ||
    payWithTokenLoading;

  const specialTariffsToRender = getSiteSpecialTariffs(site);

  return (
    <Stack spacing={2} width={'100%'} textAlign={'center'}>
      <VrmComponent vrm={vrm} setVrm={setVrm} disabled={false} />
      <Button
        variant="contained"
        color="primary"
        disabled={vrm === ''}
        onClick={handleSearch}
      >
        {loadingSession ? <ClipLoader color={'white'} /> : t('search')}
      </Button>
      {error && <Typography color={'red'}>{error}</Typography>}
      {error &&
        error ===
          (t('noSessionFoundSelectParkingTime') || 'No session found') && (
          <Home postPaymentSite={site} postPaymentVrm={vrm} />
        )}
      {loadingSession && <ClipLoader size={20} color={'white'} />}
      {carSession && (
        <Stack spacing={2} alignItems={'center'}>
          <Typography
            color={colors.blue}
            fontWeight={'bold'}
            fontSize={'1.5rem'}
          >
            {t('enteredAt')}
          </Typography>
          <Typography color={colors.blue} fontSize={'1.5rem'}>
            {DateTime.fromSeconds(carSession.startTime, {
              zone: ZONE,
            }).toFormat('yyyy-MM-dd hh:mm a')}
          </Typography>
          {!carSession.noCharge && (
            <FormGroup>
              <Stack
                direction={'row'}
                alignItems="center"
                justifyContent={'space-between'}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCreditCardChange}
                      checked={checkedCreditCard}
                    />
                  }
                  label={t('storeOrUseCards')}
                />
                <Tooltip text={t('cardStorageTooltip')} />
              </Stack>
              {receiveSms && (
                <PhoneComponent phone={phone} setPhone={setPhone} />
              )}
              {!receiveEmails && step === 'paytoken' && (
                <EmailComponent email={email} setEmail={setEmail} />
              )}
            </FormGroup>
          )}
          {selectedFee === FeeType.SpecialTariff && (
            <Stack
              alignItems={'center'}
              height="5rem"
              justifyContent={'center'}
            >
              {getSpecialTariffText(
                site,
                specialTariffsToRender,
                specialTariffSelected,
                specialTariffMessage
              )}
            </Stack>
          )}
          {showPaymentFlow && (
            <PaymentFlow
              site={site}
              details={{
                vrm,
                name: '',
                email,
                phone,
                duration: carSession.duration,
                receiveEmails,
                receiveSms,
                reachedMaxDuration: false,
                specialTariffSelected,
                selectedFee,
                checkedCreditCard,
                selectedCard,
                startTime: carSession.startTime,
              }}
              setStep={setStep}
              processPaymentCallback={processPayment}
              setVrm={setVrm}
              open={showPaymentFlow}
              onClose={() => setShowPaymentFlow(false)}
              amount={carSession.amount}
              paymentLinkLoading={paymentLinkLoading}
            />
          )}
          {(carSession.entitlementGrantedMinutes > 0 ||
            (carSession.noCharge && site.hasFreeTime && site.freeTime > 0)) && (
            <Stack spacing={0.5}>
              {carSession.entitlementGrantedMinutes > 0 && (
                <Typography color={colors.blue} fontSize="1rem">
                  {t('entitlementMinutesApplied', {
                    minutes: carSession.entitlementGrantedMinutes,
                  })}
                </Typography>
              )}
              <Typography color={colors.blue} fontSize="1rem">
                {t('firstMinutesFreeApplied', {
                  minutes:
                    site.hasFreeTime && site.freeTime > 0 ? site.freeTime : 30,
                })}
              </Typography>
            </Stack>
          )}
          {carSession.noCharge ? (
            <Stack
              width={'100%'}
              spacing={0.5}
              alignItems={'center'}
              padding={2}
              borderRadius={1}
              border={`2px solid ${FREE_GREEN}`}
            >
              <Stack direction={'row'} spacing={1} alignItems={'center'}>
                <CheckCircle sx={{ color: FREE_GREEN }} />
                <Typography
                  color={FREE_GREEN}
                  fontWeight={'bold'}
                  fontSize={'1.25rem'}
                >
                  {t('noPaymentRequired')}
                </Typography>
              </Stack>
              <Typography
                color={FREE_GREEN}
                fontWeight={'bold'}
                fontSize={'2rem'}
              >
                $0.00
              </Typography>
              <Typography color={colors.blue} fontSize={'1rem'}>
                {t('stayCoveredInFull')}
              </Typography>
            </Stack>
          ) : (
            <Button
              fullWidth
              disabled={!canBuy}
              ref={bottomRef}
              sx={{ padding: 2 }}
              variant="contained"
              onClick={() =>
                checkedCreditCard
                  ? processPayment(site, {
                      vrm,
                      name: '',
                      email,
                      phone,
                      duration: carSession.duration,
                      receiveEmails,
                      receiveSms,
                      reachedMaxDuration: false,
                      specialTariffSelected,
                      selectedFee,
                      checkedCreditCard,
                      selectedCard,
                      startTime: carSession.startTime,
                    })
                  : setShowPaymentFlow(true)
              }
            >
              {loadingButton ? (
                <ClipLoader color="white" />
              ) : step === 'token' ? (
                t('validate')
              ) : (
                t('payAmount', { amount: carSession.amount.toFixed(2) })
              )}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};
